import axios from 'axios';
import { PaginatedResponse, Apartment, Bill, PaginationParams, User, Building } from '../types';
import { buildingsApi as buildingsApiClient } from './buildingsApi';

// Resolve API base URL safely for LAN/mobile access
const envBase = process.env.REACT_APP_API_URL;
let resolvedBase = envBase || '';
// If not set or pointing to localhost while app is opened from another host, derive from window.location
if (!resolvedBase || resolvedBase.includes('localhost') || resolvedBase.includes('127.0.0.1')) {
  resolvedBase = `${window.location.protocol}//${window.location.hostname}:3000/api`;
}

const api = axios.create({ baseURL: resolvedBase });

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const authApi = {
  firstTime: async () => {
    const response = await api.get<{ firstTime: boolean }>('/auth/first-time');
    return response.data;
  },
  firstTimeLogin: async (usernameOrEmail: string, password: string) => {
    const response = await api.post('/auth/first-time-login', { username: usernameOrEmail, password });
    return response.data;
  },
  login: async (usernameOrEmail: string, password: string) => {
    const response = await api.post('/auth/login', { username: usernameOrEmail, password });
    return response.data;
  },
  register: async (email: string, password: string, name: string, username: string, role?: User['role']) => {
    const response = await api.post('/auth/register', { email, password, name, username, role });
    return response.data;
  },
  getCurrentUser: async () => {
    const response = await api.get<User>('/auth/me');
    return response.data;
  },
  updateProfile: async (data: { name?: string; phoneNumber?: string }) => {
    const response = await api.patch<User>('/auth/me', data);
    return response.data;
  },
  changePassword: async (currentPassword: string, newPassword: string) => {
    const response = await api.put('/auth/change-password', { currentPassword, newPassword });
    return response.data;
  },
  forgotPassword: async (email: string) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },
  resetPassword: async (token: string, newPassword: string) => {
    const response = await api.post('/auth/reset-password', { token, newPassword });
    return response.data;
  },
};

export const usersApi = {
  getAll: async (params: PaginationParams) => {
    const response = await api.get<PaginatedResponse<User>>('/users', { params });
    return response.data;
  },
  create: async (data: Omit<User, 'id' | 'createdAt' | 'updatedAt'>) => {
    const response = await api.post<User>('/users', data);
    return response.data;
  },
  update: async (id: string, data: Partial<User>) => {
    const response = await api.patch<User>(`/users/${id}`, data);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  },
};

export const buildingsApi = {
  getAll: async (params: PaginationParams) => {
    return buildingsApiClient.listBuildings({
      page: params.page,
      limit: params.limit,
      search: params.search,
    });
  },
  getOne: async (id: string) => {
    const response = await api.get<Building>(`/buildings/${id}`);
    return response.data;
  },
  create: async (data: Omit<Building, 'id' | 'createdAt' | 'updatedAt' | 'operator' | 'bqlUsers' | 'apartments'>) => {
    const response = await api.post<Building>('/buildings', data);
    return response.data;
  },
  update: async (id: string, data: Partial<Building>) => {
    const response = await api.patch<Building>(`/buildings/${id}`, data);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete(`/buildings/${id}`);
    return response.data;
  },
  addBQL: async (buildingId: string, userId: string) => {
    const response = await api.post(`/buildings/${buildingId}/bql/${userId}`);
    return response.data;
  },
  removeBQL: async (buildingId: string, userId: string) => {
    const response = await api.delete(`/buildings/${buildingId}/bql/${userId}`);
    return response.data;
  },
};

export const apartmentsApi = {
  getAll: async (params: PaginationParams) => {
    const response = await api.get<PaginatedResponse<Apartment>>('/apartments', { params });
    return response.data;
  },
  getOne: async (id: string) => {
    const response = await api.get<Apartment>(`/apartments/${id}`);
    return response.data;
  },
  create: async (data: Omit<Apartment, 'id' | 'createdAt' | 'updatedAt' | 'residents' | 'bills' | 'building'>) => {
    const response = await api.post<Apartment>('/apartments', data);
    return response.data;
  },
  update: async (id: string, data: Partial<Apartment>) => {
    const response = await api.patch<Apartment>(`/apartments/${id}`, data);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete(`/apartments/${id}`);
    return response.data;
  },
  addResident: async (apartmentId: string, userId: string) => {
    const response = await api.post(`/apartments/${apartmentId}/residents/${userId}`);
    return response.data;
  },
  removeResident: async (apartmentId: string, userId: string) => {
    const response = await api.delete(`/apartments/${apartmentId}/residents/${userId}`);
    return response.data;
  },
};

export const billsApi = {
  getAll: async (params: PaginationParams) => {
    const response = await api.get<PaginatedResponse<Bill>>('/bills', { params });
    return response.data;
  },
  getOne: async (id: string) => {
    const response = await api.get<Bill>(`/bills/${id}`);
    return response.data;
  },
};

// Export the new API modules
export { functionsApi } from './functionsApi';
export type { SystemFunction, CreateFunctionDto, UpdateFunctionDto } from './functionsApi';
export { buildingsApiClient as buildingsApiNew };
export const loadBuildingsForSelector = () => buildingsApiClient.getBuildings();
export type { Building, CreateBuildingDto, UpdateBuildingDto, Operator } from './buildingsApi';
export { accountsApi } from './accountsApi';
export type { Account, CreateAccountData, UpdateAccountData, PermissionGroupOption } from './accountsApi';
export { permissionGroupsApi } from './permissionGroupsApi';
export type { PermissionGroup, PermissionGroupData, PermissionData, SystemFunction as PermissionSystemFunction, ActionOption } from './permissionGroupsApi';
export { pdfTemplatesApi } from './pdfTemplatesApi';
export type { PdfTemplate, CreatePdfTemplateDto, UpdatePdfTemplateDto, SeedDefaultTemplatesResponse } from './pdfTemplatesApi';
export { buildingUtilityApi } from './buildingUtilityApi';
export type { BuildingUtilityGroup, BuildingUtility, BuildingUtilityBooking, CreateUtilityGroupRequest, UpdateUtilityGroupRequest } from './buildingUtilityApi';
export { dashboardApi } from './dashboardApi';
export type { DashboardStats, RecentActivity, TaskSummary, FeedbackSummary, BookingSummary, RevenueData, DashboardData } from './dashboardApi';
export { notificationsApi } from './notificationsApi';
export type { AppNotification, AppNotificationType, NotificationCounts } from './notificationsApi';

// Export the base api instance
export { api };